'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import useConfig from '@/hooks/useConfig';
import { useWidgetStore } from './widget.provider';
import { cleanObject } from '@repo/common/helpers';
import { useDidomi } from './didomi.provider';
import { TrackingEvent, TrackingProps } from '@/interfaces/tracking.interface';
import { ThirdPartyServiceName } from '@/config/third-parties.config';
import { useLocale } from 'next-intl';
import { useSession } from 'next-auth/react';
import { RoutesEnum } from '@/enums/routes.enum';
import { StepIdEnum } from '@/config/steps.config';

interface TrackingAnalyticsContextType {
  isReady: boolean;
  trackEvent: (trackEvent: TrackingEvent, props?: TrackingProps) => void;
}

interface DidomiProviderProps {
  children: ReactNode;
}

const SERVICE_NAME = ThirdPartyServiceName.GOOGLE_TAG_MANAGER;

const TrackingAnalyticsContext = createContext<
  TrackingAnalyticsContextType | undefined
>(undefined);

export const TrackingAnalyticsProvider = ({
  children,
}: DidomiProviderProps) => {
  const { scriptsEnabled } = useDidomi();
  const { currentStep, mode } = useConfig();
  const locale = useLocale();
  const params = useParams();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const shop = useWidgetStore((state) => state?.shop);
  const { data: session, status } = useSession();
  const [isOpened, setIsOpened] = useState<boolean>();
  const pendingEvents = useRef<
    { eventName: TrackingEvent; props: TrackingProps }[]
  >([]);

  const enabled = useMemo(() => {
    return scriptsEnabled?.includes(SERVICE_NAME);
  }, [scriptsEnabled]);

  const pageName = useMemo(() => {
    if (pathname?.includes(RoutesEnum.CONFIRM)) {
      return undefined;
    }

    if (pathname?.includes('/cancelled')) {
      return 'order_cancelled';
    }

    if (pathname?.includes(RoutesEnum.ORDERS)) {
      return StepIdEnum.CONFIRM;
    }

    return currentStep?.id;
  }, [currentStep, pathname]);

  const globalProperties = useMemo(() => {
    return {
      country_code: params?.countryCode,
      locale_code: locale,
      booking_mode: mode,
      user_id: session?.token?.sub,
      page_name: pageName,
    };
  }, [pageName, session, mode, locale, params]);

  const trackEvent = useCallback(
    (eventName: TrackingEvent, props: TrackingProps = {}) => {
      if (!enabled) {
        pendingEvents.current.push({
          eventName,
          props,
        });
        return;
      }

      if (globalThis?.window?.dataLayer) {
        globalThis?.window?.dataLayer?.push({
          event: eventName,
          ...globalProperties,
          ...props,
        });
      }
    },
    [enabled, globalProperties],
  );

  useEffect(() => {
    if (isOpened) {
      return;
    }

    if (enabled && currentStep) {
      setIsOpened(true);
    }
  }, [enabled, currentStep, isOpened, setIsOpened]);

  useEffect(() => {
    if (!isOpened) {
      return;
    }

    const excludeRoutes = [
      RoutesEnum.CONFIRM,
      RoutesEnum.ORDER,
      RoutesEnum.SUCCESS,
    ];
    const isRouteToExclude = excludeRoutes?.some((route) => {
      return pathname?.includes(route);
    });

    if (!isRouteToExclude) {
      trackEvent(
        TrackingEvent.WIDGET_OPENED,
        cleanObject({
          mode_model_id: searchParams?.get('modelId') ?? undefined,
          mode_store_id: searchParams?.get('storeId') ?? undefined,
          mode_supermodel_id: searchParams?.get('superModelId') ?? undefined,
          page_name: currentStep?.id,
          store_id: shop?.id,
          store_name: shop?.attributes?.name ?? shop?.storeId,
        }),
      );
    }
  }, [isOpened]);

  useEffect(() => {
    if (!enabled || !pageName) {
      return;
    }

    trackEvent(TrackingEvent.STEP_VIEWED, {
      page_name: pageName,
    });
  }, [enabled, pageName, trackEvent]);

  useEffect(() => {
    if (enabled && pendingEvents.current.length > 0) {
      pendingEvents.current.forEach(({ eventName, props }) => {
        trackEvent(eventName, props);
      });
      pendingEvents.current = []; // Clear the queue
    }
  }, [enabled, trackEvent]);

  const value = useMemo(
    () => ({
      isReady: status !== 'loading',
      trackEvent,
    }),
    [status, trackEvent],
  );

  return (
    <TrackingAnalyticsContext.Provider value={value}>
      {children}
    </TrackingAnalyticsContext.Provider>
  );
};

export const useTrackingAnalytics = () => {
  const context = useContext(TrackingAnalyticsContext);
  if (!context) {
    throw new Error('Missing TrackingAnalyticsProvider');
  }
  return context;
};
